/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Line } from 'react-chartjs-2';
import profileState from 'Stores/profile';
import { ReactComponent as WarningIcon } from 'Assets/svg/warning-rounded-icon.svg';
import DotIcon from 'Assets/png/green-dot.png';
import BlueDotIcon from 'Assets/png/blue-dot.png';
import YellowDotIcon from 'Assets/png/yellow-dot.png';
import DateRangePicker from 'Components/common/DateRangePicker/dateRangePicker';
import Select from 'Components/common/select';
import LoadingWrapper from 'Components/common/loadingWrapper';
import Utils from 'Utils/utils';
import { monthNames } from 'Utils/constants';
import EmptyState from './emptyState';
import './APIChart.scss';

const APILineChartOptions = {
  tension: 0.3,
  responsive: true,
  maintainAspectRatio: true,
  clip: false,
  hover: {
    intersect: false,
  },
  elements: {
    point: {
      radius: 0,
      pointStyle: () => {
        const img = new Image();
        img.width = 12;
        img.height = 12;
        img.src = DotIcon;
        return img;
      },
    },
  },
  scales: {
    x: {
      offset: true,
      grid: {
        display: false,
      },
      ticks: {
        maxTicksLimit: 15,
        color: 'rgb(28, 39, 94)',
      },
    },
    y: {
      ticks: {
        maxTicksLimit: 6,
        color: 'rgb(28, 39, 94)',
        padding: 10,
      },
      beginAtZero: true,
      grid: {
        drawBorder: false,
      },
    },
  },
  plugins: {
    datalabels: {
      display: false,
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
    legend: {
      position: 'bottom',
      labels: {
        padding: 20,
        boxWidth: 15,
        boxHeight: 10,
        color: 'rgb(28, 39, 94)',
        font: {
          size: 13,
          lineHeight: 1.33,
        },
      },
    },
  },
};

// https://jsfiddle.net/h4n8xzk3/1/
const plugins = [
  {
    afterDraw: chart => {
      const {
        tooltip,
        scales: { y },
        ctx,
      } = chart;

      if (tooltip?._active?.length) {
        const { _active } = tooltip || {};
        const { x } = _active[0].element;
        const yAxis = y;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, yAxis.top);
        ctx.setLineDash([2, 2]);
        ctx.lineTo(x, yAxis.bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#FB7D7D';
        ctx.stroke();
        ctx.restore();
      }
    },
  },
];

const getStringFormat = (type, date) => {
  if (type === 'MONTHLY')
    return `${monthNames[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;

  return `${date.getDate()} ${monthNames[date.getMonth()].slice(
    0,
    3,
  )} ${date.getFullYear()}`;
};

const getData = ({ labels, obj, key, type, analyticsData }) => {
  return labels.forEach(label => {
    obj.push(
      analyticsData.find(usageData => {
        const sDate = new Date(usageData.startDate);
        return getStringFormat(type, sDate) === label;
      })?.[key] || 0,
    );
  });
};

const ChartCard = observer(props => {
  const { title, startDate, endDate, defaultFrequency, apiType } = props;

  const defaultDateValue = {
    startDate: Number(startDate),
    endDate: Number(endDate),
    key: 'selection',
  };

  const [dateRange, setDateRange] = useState(defaultDateValue);
  const [loading, setLoading] = useState(false);
  const [frequency, setFrequency] = useState(defaultFrequency);
  const [chartData, setChartData] = useState({ labels: [], data: [] });

  useEffect(() => {
    const data = [];
    if (apiType === 'search')
      data.push({
        label: 'Search API Used',
        data: [],
        borderColor: '#81D9C3',
        backgroundColor: '#81D9C3',
      });
    else if (apiType === 'purchase')
      data.push(
        {
          label: 'Total Purchased',
          data: [],
          borderColor: '#81D9C3',
          backgroundColor: '#81D9C3',
        },
        {
          label: 'Credits Reduced',
          data: [],
          borderColor: '#F7D17C',
          backgroundColor: '#F7D17C',
          pointStyle: () => {
            const img = new Image();
            img.width = 12;
            img.height = 12;
            img.src = YellowDotIcon;
            return img;
          },
        },
      );
    else if (apiType === 'enrich')
      data.push(
        {
          label: 'Enrichment Requested',
          data: [],
          borderColor: '#81D9C3',
          backgroundColor: '#81D9C3',
        },
        {
          label: 'Enriched',
          data: [],
          borderColor: '#A19EF8',
          backgroundColor: '#A19EF8',
          pointStyle: () => {
            const img = new Image();
            img.width = 12;
            img.height = 12;
            img.src = BlueDotIcon;
            return img;
          },
        },
        {
          label: 'Credits Reduced',
          data: [],
          borderColor: '#F7D17C',
          backgroundColor: '#F7D17C',
          pointStyle: () => {
            const img = new Image();
            img.width = 12;
            img.height = 12;
            img.src = YellowDotIcon;
            return img;
          },
        },
      );
    const labels = [];

    const endDateForCondition = new Date(dateRange.endDate);
    const adjustedEndDate = new Date(
      endDateForCondition.getFullYear(),
      endDateForCondition.getMonth(),
      endDateForCondition.getDate(),
      23,
      59,
      59,
    );

    if (frequency === 'MONTHLY') {
      const startDateLoop = new Date(dateRange.startDate);
      while (startDateLoop < adjustedEndDate) {
        labels.push(getStringFormat(frequency, startDateLoop));
        startDateLoop.setMonth(startDateLoop.getMonth() + 1);
      }
    } else if (frequency === 'WEEKLY') {
      const startDateLoop = new Date(dateRange.startDate);
      startDateLoop.setDate(startDateLoop.getDate() - startDateLoop.getDay());
      while (startDateLoop < adjustedEndDate) {
        labels.push(getStringFormat(frequency, startDateLoop));
        startDateLoop.setDate(startDateLoop.getDate() + 7);
      }
    } else if (frequency === 'DAILY') {
      const startDateLoop = new Date(dateRange.startDate);
      while (startDateLoop < adjustedEndDate) {
        labels.push(getStringFormat(frequency, startDateLoop));
        startDateLoop.setDate(startDateLoop.getDate() + 1);
      }
    }

    if (labels.length === 1)
      data.forEach(prop => {
        // eslint-disable-next-line no-param-reassign
        prop.pointRadius = 10;
      });
    else
      data.forEach(prop => {
        // eslint-disable-next-line no-param-reassign
        prop.pointRadius = 0;
      });

    const dataToPass = {
      analyticsData: profileState.APIUsageAnalyticsData?.[apiType] || [],
      labels,
      type: frequency,
    };
    if (apiType === 'search') {
      getData({ obj: data[0].data, key: 'searchAPI', ...dataToPass });
    } else if (apiType === 'purchase') {
      getData({
        obj: data[0].data,
        key: 'purchaseAPI',
        ...dataToPass,
      });
      getData({
        obj: data[1].data,
        key: 'purchaseCreditReduced',
        ...dataToPass,
      });
    } else if (apiType === 'enrich') {
      getData({
        obj: data[0].data,
        key: 'enrichRequested',
        ...dataToPass,
      });
      getData({
        obj: data[1].data,
        key: 'enrichAPI',
        ...dataToPass,
      });
      getData({
        obj: data[2].data,
        key: 'enrichCreditReduced',
        ...dataToPass,
      });
    }
    setChartData({ labels, data });
  }, [profileState.APIUsageAnalyticsData, frequency, dateRange]);

  return (
    <div className="chart-wrapper">
      <div className="api-top-wrapper">
        <div className="title">{title}</div>
        <div className="right-section">
          <DateRangePicker
            defaultValue={dateRange}
            editableDateInputs={false}
            closeIcon={false}
            onClose={() => {
              setDateRange(defaultDateValue);
            }}
            maxDate={endDate}
            onSubmit={async range => {
              setLoading(true);
              const newValue = {
                startDate: Number(range.startDate),
                endDate: Number(range.endDate),
              };
              await profileState.getAPIUsageAnalytics(
                {
                  frequency,
                  ...newValue,
                },
                apiType,
              );
              setLoading(false);
              setDateRange(newValue);
            }}
          />
          <Select
            options={[
              { label: 'Monthly', value: 'MONTHLY' },
              { label: 'Weekly', value: 'WEEKLY' },
              { label: 'Daily', value: 'DAILY' },
            ]}
            value={frequency}
            label="Frequency"
            onChange={async value => {
              setLoading(true);
              setFrequency(value);
              await profileState.getAPIUsageAnalytics(
                {
                  frequency: value,
                  ...dateRange,
                },
                apiType,
              );
              setLoading(false);
            }}
          />
        </div>
      </div>
      <div className="chart-tooltip" />
      <LoadingWrapper position="absolute" show={loading} />
      <div className="chart-container">
        {profileState.APIUsageAnalyticsData?.[apiType]?.length > 0 &&
        !profileState.APIUsageAnalyticsData?.[apiType]?.every(usageData => {
          if (apiType === 'search')
            return usageData.searchAPI === 0 || usageData.searchAPI === null;
          if (apiType === 'enrich')
            return (
              (usageData.enrichRequested === 0 ||
                usageData.enrichRequested === null) &&
              (usageData.enrichAPI === 0 || usageData.enrichAPI === null) &&
              (usageData.enrichCreditReduced === 0 ||
                usageData.enrichCreditReduced === null)
            );
          return (
            (usageData.purchaseAPI === 0 || usageData.purchaseAPI === null) &&
            (usageData.purchaseCreditReduced === 0 ||
              usageData.enrichAPI === null)
          );
        }) ? (
          <Line
            options={APILineChartOptions}
            plugins={plugins}
            data={{
              labels: chartData.labels,
              datasets: chartData.data,
            }}
          />
        ) : (
          <EmptyState chart="line" title="No data available" />
        )}
      </div>
      {frequency === 'WEEKLY' && (
        <i className="note">
          <sup>*</sup>Date represents starting date of the week
        </i>
      )}
    </div>
  );
});

const APIUsageChart = () => {
  const [loading, setLoading] = useState(false);

  const defaultFrequency = 'MONTHLY';
  const currentDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setMonth(defaultStartDate.getMonth() - 6);

  async function getAPIData() {
    setLoading(true);
    await profileState.getAPIUsageAnalytics({
      frequency: defaultFrequency,
      startDate: Number(defaultStartDate),
      endDate: Number(currentDate),
    });
    setLoading(false);
  }

  useEffect(() => {
    getAPIData();
  }, []);

  return (
    <div className="usage-analytics-page">
      <div className="api-usage-wrap">
        <LoadingWrapper position="fixed" show={loading} />
        <div className="credits-card">
          <div className="top-wrap">
            <div className="page-title">API Usage Overview</div>
            <div className="billing-cycle">
              {profileState.usageAnalyticsData.subscriptionStartDate &&
              profileState.usageAnalyticsData.subscriptionRenewalDate
                ? `Billing Cycle: 
            ${Utils.getFormattedDate(
              profileState.usageAnalyticsData.subscriptionStartDate,
            )} - ${Utils.getFormattedDate(
                    profileState.usageAnalyticsData.subscriptionRenewalDate,
                  )}`
                : ''}
            </div>
          </div>
          <div className="credit-values">
            {profileState.APIUsageAnalyticsData.allottedSearchAPICredits >
              0 && (
              <div className="title-with-value">
                <span className="title">Search API Credits</span>
                <span className="value">
                  <span>
                    {profileState.APIUsageAnalyticsData.searchAPICreditsUsed
                      ? Utils.numberFormat(
                          profileState.APIUsageAnalyticsData
                            .searchAPICreditsUsed,
                        )
                      : 0}
                  </span>
                  <span>/</span>
                  <span>
                    {Utils.numberFormat(
                      profileState.APIUsageAnalyticsData
                        .allottedSearchAPICredits,
                    )}
                  </span>
                </span>
              </div>
            )}
            {profileState.APIUsageAnalyticsData.allottedPurchaseAPICredits >
              0 && (
              <div className="title-with-value">
                <span className="title">Purchase API Credits</span>
                <span className="value">
                  <span>
                    {profileState.APIUsageAnalyticsData.purchaseAPICreditsUsed
                      ? Utils.numberFormat(
                          profileState.APIUsageAnalyticsData
                            .purchaseAPICreditsUsed,
                        )
                      : 0}
                  </span>
                  <span>/</span>
                  <span>
                    {Utils.numberFormat(
                      profileState.APIUsageAnalyticsData
                        .allottedPurchaseAPICredits,
                    )}
                  </span>
                </span>
              </div>
            )}
            {profileState.APIUsageAnalyticsData.allottedEnrichAPICredits >
              0 && (
              <div className="title-with-value">
                <span className="title">Enrich API Credits</span>
                <span className="value">
                  <span>
                    {profileState.APIUsageAnalyticsData.enrichAPICreditsUsed
                      ? Utils.numberFormat(
                          profileState.APIUsageAnalyticsData
                            .enrichAPICreditsUsed,
                        )
                      : 0}
                  </span>
                  <span>/</span>
                  <span>
                    {Utils.numberFormat(
                      profileState.APIUsageAnalyticsData
                        .allottedEnrichAPICredits,
                    )}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="api-note-wrapper">
          <div className="icon-wrapper">
            <WarningIcon />
          </div>
          <p>
            To ensure you get the most out of your analytics, we recommend
            migrating to API version 3. This will provide you with valuable
            insights into your API usage.
          </p>
        </div>
        {profileState.apiUsedForChart.map(api => {
          const { key, title } = api;
          return (
            <ChartCard
              apiType={key}
              key={key}
              title={title}
              startDate={defaultStartDate}
              defaultFrequency={defaultFrequency}
              endDate={currentDate}
            />
          );
        })}
      </div>
    </div>
  );
};

export default APIUsageChart;
